import { Component, computed, inject, input, LOCALE_ID } from '@angular/core';
import { RouterLink, UrlTree } from '@angular/router';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  DmAccordion,
  DmAccordionContent,
  DmAccordionItem,
  DmAccordionTrigger,
  DmAvatar,
  DmCard,
  DmCardBody,
  DmIcon,
  DmTooltip,
} from '@digitale-menschen/component-library-angular';
import { AvatarFromPersonDirective } from '../../directives/avatar-from-person.directive';
import { resolvePersonName } from '../../functions/person.function';
import { VerlaufTimelineEntry } from './verlauf-timeline-entry.model';
import { formatVerlaufTimelineTimestamp } from './verlauf-timeline-timestamp';

/**
 * One entry of a Verlauf timeline. Every entry is a marker in the gutter and a
 * line beside it — who did it, what happened, when — centred on the marker. A
 * `card` entry adds a bordered box beneath that line, holding only its body (a
 * note, a diff); an `item` is the line alone (a recorded change).
 *
 * Content slots:
 * - `title-extra` — inline content after the headline, e.g. the tags in
 *   «Status geändert von `Neu` zu `In Arbeit`»
 * - `trailing` — an action right-aligned on the line, before the menu. Not offered
 *   by a collapsible card, for the reason the menu is not.
 * - `menu` — the «…» menu, right-aligned on the line. Not offered by a collapsible
 *   card, whose line is the accordion's trigger button — a menu nested in a button
 *   is not operable.
 * - `subline` — a muted line below the headline, e.g. the reason a change the
 *   user never made happened anyway; the `item` variant only
 * - `footer` — a band closing the box, laid out full width on its own surface.
 *   The projected node has to carry `footer` itself. The `card` variant only, and
 *   inside the disclosure of a collapsible one, so it folds away with the body.
 * - the default slot — the box's content; ignored by the `item` variant
 */
@Component({
  selector: 'lp-verlauf-timeline-entry',
  imports: [
    DmAvatar,
    AvatarFromPersonDirective,
    DmCard,
    DmCardBody,
    DmAccordion,
    DmAccordionItem,
    DmAccordionTrigger,
    DmAccordionContent,
    DmIcon,
    NgTemplateOutlet,
    DmTooltip,
    RouterLink,
  ],
  templateUrl: './verlauf-timeline-entry.component.html',
  styleUrl: './verlauf-timeline-entry.component.scss',
  host: {
    class: 'lp-verlauf-timeline-entry',
    '[class.lp-verlauf-timeline-entry--last]': 'isLast()',
    '[class.lp-verlauf-timeline-entry--compact]': 'compact()',
  },
})
export class VerlaufTimelineEntryComponent {
  public readonly entry = input.required<VerlaufTimelineEntry>();
  /** The last entry draws no connector below itself. */
  public readonly isLast = input<boolean>(false);
  /**
   * Turns the line into an accordion trigger that folds the box away. A
   * long body — the diff of a changed description — starts folded, so the timeline
   * stays readable. Has no effect on the `item` variant, which has no body.
   */
  public readonly collapsible = input<boolean>(false);
  /**
   * Drops the avatar, the connector and the meta line, leaving the entry as its
   * headline and its body alone. Set on the entries of a
   * `lp-verlauf-timeline-group`, which carries those for the whole group.
   */
  public readonly compact = input<boolean>(false);
  /**
   * Makes the box a link to what the entry records, which `dm-card` tints on hover. The `card`
   * variant only, and not a collapsible one, whose line is the accordion trigger.
   */
  public readonly link = input<UrlTree | null>(null);
  /** The link's accessible name; required wherever {@link link} is set. */
  public readonly linkLabel = input<string>('');
  /**
   * Why the box leads nowhere, shown on hover and on focus. The `card` variant only, and not
   * together with {@link link}; the box becomes focusable so the keyboard reaches it.
   */
  public readonly hint = input<string>('');
  /** Whether the body is being edited in place; the editor then takes the box's place. */
  public readonly isEditing = input<boolean>(false);

  protected readonly isCard = computed<boolean>(() => this.entry().variant === 'card');
  protected readonly iconName = computed<string | null>(() => this.entry().icon ?? null);

  protected readonly authorName = computed<string>(() => resolvePersonName(this.entry().author));
  /** Inside a group the author and the date belong to the group, not to the part. */
  protected readonly showsAuthorName = computed<boolean>(() => !this.compact() && !!this.authorName());
  /** Only where the kind's icon holds the gutter, which is then not the avatar. */
  protected readonly showsAuthorAvatar = computed<boolean>(() => this.showsAuthorName() && !!this.iconName());

  protected readonly timestampLabel = computed<string>(() =>
    this.compact() ? '' : this.entry().dateLabel || formatVerlaufTimelineTimestamp(this.entry().timestamp, this.datePipe),
  );

  private readonly datePipe = new DatePipe(inject(LOCALE_ID));
}
